import { useCallback, useEffect, useRef, useState } from "react";
import { driver, type DriveStep, type Driver } from "driver.js";
import "driver.js/dist/driver.css";
import "@/product-tour.css";
import { completeTour as completeTourApi } from "@/lib/api/users";
import { useAuth } from "@/lib/auth/auth-provider";

const WELCOME_HEADER_HTML = `
<div class="tour-welcome-header">
  <img src="/logo-with-text-blue.png" alt="C2 Vault" class="tour-welcome-logo" />
  <div class="tour-welcome-icon">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor">
      <path d="M200,24H56A16,16,0,0,0,40,40V216a16,16,0,0,0,16,16H200a16,16,0,0,0,16-16V40A16,16,0,0,0,200,24Zm0,192H56V40H200ZM176,88a48,48,0,0,1-96,0,8,8,0,0,1,16,0,32,32,0,0,0,64,0,8,8,0,0,1,16,0Zm-16,72a8,8,0,0,1-8,8H104a8,8,0,0,1,0-16h48A8,8,0,0,1,160,160Zm0,32a8,8,0,0,1-8,8H104a8,8,0,0,1,0-16h48A8,8,0,0,1,160,192Z"/>
    </svg>
  </div>
</div>`;

function buildSteps(): DriveStep[] {
  return [
    {
      popover: {
        title: "Welcome!",
        description:
          "Let's take a quick tour to help you get started with your pharmacy compliance dashboard.",
        popoverClass: "tour-welcome",
        nextBtnText: "Start Tour",
        showButtons: ["next"],
      },
    },
    {
      element: '[data-tour="kpi-stats"]',
      popover: {
        title: "Dashboard Overview",
        description:
          "Your key metrics at a glance — total inventory, today's dispenses and receipts, and pending reminders. Click any card to jump to that section.",
      },
    },
    {
      element: '[data-tour="quick-dispense"]',
      popover: {
        title: "Quick Dispense",
        description:
          "Need to log a dispense fast? Use this button to search your inventory and record a dispense from anywhere.",
      },
    },
    {
      element: '[data-tour="nav-inventory"]',
      popover: {
        title: "Inventory Management",
        description:
          "View and manage your full controlled substance inventory. Add new items, update quantities, and track stock levels.",
      },
    },
    {
      element: '[data-tour="nav-dispenses"]',
      popover: {
        title: "Dispenses",
        description:
          "Record outgoing medications with complete chain-of-custody tracking. Every dispense is logged for compliance.",
      },
    },
    {
      element: '[data-tour="nav-receipts"]',
      popover: {
        title: "Receipts",
        description:
          "Log incoming inventory from your wholesalers. Keep accurate records of all received controlled substances.",
      },
    },
    {
      element: '[data-tour="nav-reports"]',
      popover: {
        title: "Compliance Reports",
        description:
          "Generate audit-ready compliance reports. Perfect for DEA inspections, internal audits, and regulatory reviews.",
      },
    },
    {
      element: '[data-tour="nav-audit"]',
      popover: {
        title: "Audit Log",
        description:
          "Every action is recorded with a detailed audit trail. See exactly who did what and when — full accountability.",
      },
    },
    {
      element: '[data-tour="notifications"]',
      popover: {
        title: "Notifications",
        description:
          "Stay on top of important alerts — low stock warnings, compliance reminders, and system notifications appear here.",
      },
    },
    {
      element: '[data-tour="help"]',
      popover: {
        title: "Help & Support",
        description:
          "Access documentation, report issues, or contact support anytime. You can also restart this tour from here.",
      },
    },
    {
      element: '[data-tour="user-profile"]',
      popover: {
        title: "Your Profile",
        description:
          "Switch between users, access settings, or log out. Each user has their own session and permissions.",
      },
    },
  ];
}

interface UseProductTourOptions {
  autoStart?: boolean;
}

export function useProductTour({
  autoStart = false,
}: UseProductTourOptions = {}) {
  const [isActive, setIsActive] = useState(false);
  const hasAutoStarted = useRef(false);
  const driverRef = useRef<Driver | null>(null);
  const auth = useAuth();

  const hasSeenTour = auth.user?.hasSeenTour ?? false;

  const markTourCompleted = useCallback(() => {
    completeTourApi()
      .then(() => auth.refetch())
      .catch((err) => {
        console.error("Failed to mark tour as completed:", err);
      });
  }, [auth]);

  const startTour = useCallback(() => {
    // Small delay to ensure all DOM elements are rendered
    setTimeout(() => {
      const allSteps = buildSteps();

      // Filter steps to only include ones whose elements exist in the DOM
      const availableSteps = allSteps.filter((step) => {
        if (!step.element) return true;
        return document.querySelector(step.element as string) !== null;
      });

      if (availableSteps.length <= 1) return;

      const tourDriver = driver({
        showProgress: true,
        animate: true,
        smoothScroll: true,
        allowClose: true,
        overlayColor: "rgb(15 23 42 / 0.5)",
        stagePadding: 8,
        stageRadius: 8,
        popoverOffset: 12,
        showButtons: ["next", "previous"],
        nextBtnText: "Next",
        prevBtnText: "Back",
        doneBtnText: "Done",
        progressText: "{{current}} of {{total}}",
        steps: availableSteps,
        onPopoverRender: (popover) => {
          const isWelcome = popover.wrapper.classList.contains("tour-welcome");

          // Inject logo + icon into welcome step
          if (isWelcome) {
            const title = popover.wrapper.querySelector(
              ".driver-popover-title"
            );
            if (
              title &&
              !popover.wrapper.querySelector(".tour-welcome-header")
            ) {
              title.insertAdjacentHTML("beforebegin", WELCOME_HEADER_HTML);
            }
          }

          // Add skip button to footer on every step
          const footer = popover.wrapper.querySelector(
            ".driver-popover-footer"
          );
          if (footer && !footer.querySelector(".tour-skip-btn")) {
            const skipBtn = document.createElement("button");
            skipBtn.className = "tour-skip-btn";
            skipBtn.textContent = "Skip tour";
            skipBtn.addEventListener("click", (e) => {
              e.stopPropagation();
              tourDriver.destroy();
            });
            footer.insertBefore(skipBtn, footer.firstChild);
          }
        },
        onDestroyStarted: () => {
          markTourCompleted();
          setIsActive(false);
          driverRef.current = null;
          tourDriver.destroy();
        },
      });

      driverRef.current = tourDriver;
      setIsActive(true);
      tourDriver.drive();
    }, 500);
  }, [markTourCompleted]);

  // Auto-start on first visit for users who haven't seen the tour
  useEffect(() => {
    if (autoStart && !hasSeenTour && !hasAutoStarted.current) {
      hasAutoStarted.current = true;
      startTour();
    }
  }, [autoStart, hasSeenTour, startTour]);

  return {
    startTour,
    isActive,
    hasSeenTour,
  };
}
